import { createContext, memo, useContext, useMemo, useRef } from 'react';
import { of, ReplaySubject, Subject } from 'rxjs';
import { usePersistFn } from '../functions/hooks/usePersistFn';
import { useLocalObservable } from '../functions/rxReact/useLocalObservable';
import { FCC } from '../utils/react';

interface PanelScrollViewProviderValue {
  bottomHeight$: Subject<number>;
  topHeight$: Subject<number>;
}

const PanelScrollViewContext =
  createContext<null | PanelScrollViewProviderValue>(null);

export const PanelScrollViewProvider: FCC = memo(props => {
  const value: PanelScrollViewProviderValue = useMemo(
    () => ({
      bottomHeight$: new ReplaySubject(1),
      topHeight$: new ReplaySubject(1),
    }),
    [],
  );

  return (
    <PanelScrollViewContext.Provider value={value}>
      {props.children}
    </PanelScrollViewContext.Provider>
  );
});

export const usePanelBottomViewHeight = (): undefined | number => {
  const ctx = useContext(PanelScrollViewContext);

  return useLocalObservable(
    () => ctx?.bottomHeight$ ?? of(undefined),
    [ctx?.bottomHeight$],
    undefined,
  );
};

export const useReportPanelBottomViewHeight = (): ((
  height: number,
) => void) => {
  const ctx = useContext(PanelScrollViewContext);
  const lastTimeValueRef = useRef<null | number>(null);

  return usePersistFn(height => {
    if (ctx) {
      if (lastTimeValueRef.current === height) return;
      ctx.bottomHeight$.next(height);
      lastTimeValueRef.current = height;
    }
  });
};

export const usePanelTopViewHeight = (): undefined | number => {
  const ctx = useContext(PanelScrollViewContext);

  return useLocalObservable(
    () => ctx?.topHeight$ ?? of(undefined),
    [ctx?.topHeight$],
    undefined,
  );
};

export const useReportPanelTopViewHeight = (): ((height: number) => void) => {
  const ctx = useContext(PanelScrollViewContext);
  const lastTimeValueRef = useRef<null | number>(null);

  return usePersistFn(height => {
    if (ctx) {
      if (lastTimeValueRef.current === height) return;
      ctx.topHeight$.next(height);
      lastTimeValueRef.current = height;
    }
  });
};
